// S11 · the candidate's assessment chain on the candidate page (CR-022: "Not Required or Required -> Draft -> Sent ->
// Submitted -> Reviewed -> Completed/Withdrawn"; "Capture sent/due/completed/outcome."; "Assigned reviewer evaluates.";
// "No assessment task when Not Required."; "Required assessment cannot be silently skipped."). It replaces the V1
// assessment section of `Candidates.tsx` and keeps its accessible heading named *Assessment*.
//
// The card reads `exercise.open` and draws what its projection carries (D13): `full` (the Hiring Manager, the owner, a
// covering grantee), `reviewer` (an assigned reviewer: the brief, the criteria, the returned work and their own review;
// another reviewer's review only once the assessment is Reviewed), `status` (the HR Business Partner, the Coordinator, the
// TA Manager: status and dates, never the returned work, the outcome or a review) and `brief` (an interviewer with an
// occurrence: the case study and its criteria). The rail is his chain; Withdrawn is its terminal branch. The one primary
// of each step: **Record** (Draft → Sent, recorded from the ATS; Sent → Submitted with the returned work through the drop
// zone), **Submit** (a reviewer's review), **Complete** with the outcome; *Withdrawn* records the withdrawal reason.
// Every control follows the tool's `can`; the server decides each call again.

import { useId, useState, type ReactNode } from "react";
import {
  ACTION_LABELS, ASSESSMENT_STATUS, CORE_LABELS, COMMON_LABELS, G1_LABELS, SCORECARD_LABELS, SURFACE_LABELS, Button, Card, Card2, Checklist, CheckRow,
  CheckTick, Dialog, DropZone, Due, EmptyState, Field, Flex, Input, KeyValue, McRoot, Paragraph, Person, Pill, Rail, RatingScale, Recorded, Row,
  Stack, Stamp, TextArea, TextLink, formatDate, formatDateTime, toEpoch, useNow, type CheckStatus, type DropZoneError, type ErrorStripProps,
  type KeyValueItem, type RailStep, type RatingValue,
} from "@/mc";
import {
  exerciseComplete, exerciseOpen, exerciseRecordReturn, exerciseRecordSent, exerciseReview, exerciseWithdraw, type ExerciseCompleteInput,
  type ExerciseReviewInput, type ExerciseWithdrawInput,
} from "@/api/tools";
import { AppError, businessLocalNow, businessLocalToISO, uploadFile } from "@/api/runtime";
import { useRead, useWrite } from "@/api/useTool";
import { ASSESSMENT_WORDS } from "./assessment/labels";
import {
  CHAIN, DISPLAY_WORD, evaluativeVisible, exerciseView, failureOf, reviewsVisible, roundNameFromKey, type AssessmentDisplay, type CriterionFinding,
  type ExerciseViewLike, type ReviewLike,
} from "./assessment/shapes";

/** The returned work's upload, as the drop zone shows it. */
export interface ReturnUpload {
  fileName?: string | undefined;
  progress?: number | undefined;
  error?: DropZoneError | undefined;
  fileId?: string | undefined;
}

export interface ReviewDraft {
  criteria_findings: CriterionFinding[];
  evaluation_text: string;
  advisory_rating: number | null;
}

export interface AssessmentChainViewProps {
  data: ExerciseViewLike | null;
  loading?: boolean | undefined;
  busy?: boolean | undefined;
  /** The server refused the last write: his rule sentence. */
  refused?: string | undefined;
  failure?: ErrorStripProps | undefined;
  saved?: boolean | undefined;
  upload?: ReturnUpload | undefined;
  /** `datetime-local` values in the business zone; the host converts and calls the tool. */
  onRecordSent?: ((sentAt: string, dueAt: string) => Promise<unknown>) | undefined;
  onFile?: ((file: File) => void) | undefined;
  onRecordReturn?: ((returnedAt: string, fileIds: string[]) => Promise<unknown>) | undefined;
  onReview?: ((review: ReviewDraft) => Promise<unknown>) | undefined;
  onComplete?: ((outcome: string) => Promise<unknown>) | undefined;
  onWithdraw?: ((reason: string) => Promise<unknown>) | undefined;
}

/** The rail's steps for a display state: done before it, on at it, skipped where a withdrawal passed them over. */
export function railSteps(e: ExerciseViewLike): { steps: RailStep[]; terminal?: { word: string } | undefined } {
  const word = (key: AssessmentDisplay) => DISPLAY_WORD[key] ?? "";
  if (e.display === "withdrawn") {
    // How far it went before the withdrawal, from the dates the projection carries.
    const reached = e.returned_at ? 2 : e.sent_at ? 1 : -1;
    const steps = CHAIN.slice(0, -1).map((key, at): RailStep => ({ label: word(key), state: at <= reached ? "done" : "skip" }));
    return { steps: [...steps, { label: word("completed") }], terminal: { word: ASSESSMENT_STATUS.withdrawn } };
  }
  const at = CHAIN.indexOf(e.display);
  const steps = CHAIN.map((key, index): RailStep => ({
    label: word(key),
    state: at < 0 ? "next" : index < at || (e.display === "completed" && index === at) ? "done" : index === at ? "on" : "next",
  }));
  return { steps };
}

const FINDING_STATUS: Readonly<Record<string, CheckStatus>> = { met: "ok", partial: "warn", not: "bad" };

function reviewerName(review: ReviewLike): string {
  const who = review.reviewer;
  return typeof who === "string" ? who : who?.name ?? "";
}

/** One review as values: the reviewer, when, the criteria findings, the evidence and the advisory rating. */
function ReviewValues({ review }: { review: ReviewLike }) {
  const name = reviewerName(review);
  return (
    <Card2>
      <Stack gap={10}>
        <Flex justify="between" align="center">
          {name ? <Person name={name} size={24} /> : <span />}
          {name ? <Stamp actor={name} at={review.submitted_at} /> : null}
        </Flex>
        {review.criteria_findings.length ? (
          <Checklist>
            {review.criteria_findings.map((finding, index) => (
              <CheckRow key={index} status={FINDING_STATUS[finding.finding] ?? "todo"} sub={finding.note || undefined}>{finding.criterion}</CheckRow>
            ))}
          </Checklist>
        ) : null}
        <Paragraph>{review.evaluation_text}</Paragraph>
        {review.advisory_rating ? <KeyValue items={[[SCORECARD_LABELS.rating, String(review.advisory_rating)]]} cols={1} /> : null}
      </Stack>
    </Card2>
  );
}

/** A reviewer's own review: one tick per criterion (ticked = met), the evidence, an advisory rating, **Submit**. */
function ReviewForm({ criteria, busy, onReview }: { criteria: readonly string[]; busy?: boolean | undefined; onReview: (review: ReviewDraft) => Promise<unknown> }) {
  const id = useId();
  const [met, setMet] = useState<readonly boolean[]>(() => criteria.map(() => false));
  const [text, setText] = useState("");
  const [rating, setRating] = useState<RatingValue | null>(null);
  const [tried, setTried] = useState(false);
  const short = text.trim().length < 3;
  return (
    <Card2 title={ACTION_LABELS.review}>
      <Stack gap={12}>
        {criteria.length ? (
          <Checklist>
            {criteria.map((criterion, index) => (
              <CheckRow
                key={index}
                status={met[index] ? "ok" : "todo"}
                control={(
                  <CheckTick
                    checked={Boolean(met[index])}
                    labelledBy={`${id}-c${index}`}
                    hs={`plan.assessment.chain.review.criterion.${index + 1}`}
                    onChange={(checked) => setMet(met.map((old, at) => (at === index ? checked : old)))}
                  />
                )}
              >
                <span id={`${id}-c${index}`}>{criterion}</span>
              </CheckRow>
            ))}
          </Checklist>
        ) : null}
        <Field label={SCORECARD_LABELS.evidence} required error={tried && short ? COMMON_LABELS.required : undefined}>
          <TextArea value={text} maxLength={8000} rows={5}  onChange={(event) => setText(event.target.value)} />
        </Field>
        <RatingScale label={SCORECARD_LABELS.rating} value={rating} onChange={setRating} />
        <Flex>
          <Button
            tone="primary"
            busy={busy}
            
            onClick={() => {
              setTried(true);
              if (short) return;
              void onReview({
                criteria_findings: criteria.map((criterion, index) => ({ criterion, finding: met[index] ? "met" : "not", note: null })),
                evaluation_text: text.trim(),
                advisory_rating: typeof rating === "number" ? rating : null,
              }).catch(() => undefined);
            }}
          >
            {ACTION_LABELS.submit}
          </Button>
        </Flex>
      </Stack>
    </Card2>
  );
}

type ChainDialog = null | "sent" | "return" | "withdraw";

/** The chain as a card: no data access, so a seat's rendering is a function of the projection it was given. */
export function AssessmentChainView({
  data, loading, busy, refused, failure, saved, upload = {}, onRecordSent, onFile, onRecordReturn, onReview, onComplete, onWithdraw,
}: AssessmentChainViewProps) {
  const now = useNow();
  const [dialog, setDialog] = useState<ChainDialog>(null);
  const [sentAt, setSentAt] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [returnedAt, setReturnedAt] = useState("");
  const [outcome, setOutcome] = useState("");
  const [reason, setReason] = useState("");
  const close = () => setDialog(null);
  const card = (children: ReactNode, pill?: ReactNode) => (
    <Card title={ASSESSMENT_WORDS.assessment} level={2} pills={pill} loading={loading ? { lines: 3 } : undefined} refused={refused ? { reason: refused } : undefined} error={failure}>
      {children}
    </Card>
  );
  if (!data) return card(null);
  const e = data;
  const word = DISPLAY_WORD[e.display] ?? null;
  const pill = word ? <Pill>{word}</Pill> : undefined;
  // undecided: the intake decision is still open; no chain is drawn.
  if (e.display === "undecided") return card(null);
  if (e.display === "not_required") return card(<EmptyState inline>{CORE_LABELS.noAssessmentTask}</EmptyState>, pill);

  const can = e.can ?? {};
  const evaluative = evaluativeVisible(e.projection);
  const { steps, terminal } = railSteps(e);
  const stage = roundNameFromKey(e.stage_round_key);
  const items: KeyValueItem[] = [];
  if (e.assessment_type) items.push([ASSESSMENT_WORDS.type, e.assessment_type]);
  if (stage) items.push([ASSESSMENT_WORDS.stage, stage]);
  if (e.owner) items.push([SURFACE_LABELS.owner, e.owner.name]);
  if (e.sent_at) items.push([ASSESSMENT_STATUS.sent, formatDate(e.sent_at)]);
  if (e.due_at) {
    const late = e.display === "sent" && toEpoch(e.due_at) < now;
    items.push([ASSESSMENT_WORDS.due, <Due tier={late ? "overdue" : "due"} phrase={formatDate(e.due_at)} at={e.due_at} />]);
  }
  if (evaluative && e.returned_at) {
    const files = e.return_file_ids ?? [];
    items.push([ASSESSMENT_STATUS.submitted, (
      <Flex gap={8} wrap>
        {files.length
          ? files.map((fileId, index) => (
            <TextLink key={fileId} href={`/api/files/${encodeURIComponent(fileId)}`}>
              {files.length > 1 ? `${formatDateTime(e.returned_at ?? "")} · ${index + 1}` : formatDateTime(e.returned_at ?? "")}
            </TextLink>
          ))
          : formatDate(e.returned_at)}
      </Flex>
    )]);
  }
  if (e.completed_at) items.push([ASSESSMENT_STATUS.completed, formatDate(e.completed_at)]);
  if (evaluative && e.completion_outcome) items.push([ASSESSMENT_WORDS.outcome, e.completion_outcome, undefined, 3]);
  if (e.projection === "full" && e.withdrawal_reason) items.push([G1_LABELS.withdrawalReason, e.withdrawal_reason, undefined, 3]);
  const briefShown = e.projection !== "status" && Boolean(e.brief_text);
  const criteria = e.criteria ?? [];
  const reviewers = evaluative ? e.reviewers ?? [] : [];
  const others = reviewsVisible(e.projection, e.display) ? e.reviews ?? [] : [];
  const mine = evaluative ? e.my_review ?? null : null;
  const otherReviews = mine ? others.filter((review) => review.submitted_at !== mine.submitted_at || reviewerName(review) !== reviewerName(mine)) : others;
  const fileReady = Boolean(upload.fileId);

  return card(
    <Stack gap={14}>
      <Rail steps={steps} terminal={terminal} label={ASSESSMENT_WORDS.assessment} wrap />
      {items.length ? <KeyValue items={items} cols={3} /> : null}
      {briefShown ? <KeyValue items={[[ASSESSMENT_WORDS.brief, e.brief_text ?? "", undefined, 3]]} cols={1} /> : null}
      {e.projection !== "status" && criteria.length > 0 && !can["review"] ? (
        <KeyValue items={[[ASSESSMENT_WORDS.criteria, criteria.join(" · ")]]} cols={1} />
      ) : null}
      {reviewers.length ? (
        <Stack gap={6}>
          {reviewers.map((reviewer) => (
            <Row key={reviewer.person_id} cols={2}>
              <Person name={reviewer.name} hueKey={reviewer.person_id} size={24} />
              {reviewer.reviewed_at ? <Stamp actor={reviewer.name} at={reviewer.reviewed_at} /> : <span />}
            </Row>
          ))}
        </Stack>
      ) : null}
      {mine ? <ReviewValues review={mine} /> : null}
      {otherReviews.map((review, index) => <ReviewValues key={`${review.submitted_at}-${index}`} review={review} />)}
      {can["review"] && !mine && onReview ? <ReviewForm criteria={criteria} busy={busy} onReview={onReview} /> : null}
      {can["complete"] && onComplete ? (
        <Card2>
          <Stack gap={10}>
            <Field label={ASSESSMENT_WORDS.outcome} required>
              <TextArea value={outcome} maxLength={1000} rows={3}  onChange={(event) => setOutcome(event.target.value)} />
            </Field>
            <Flex>
              <Button
                tone="primary"
                busy={busy}
                
                onClick={() => { if (outcome.trim().length >= 3) void onComplete(outcome.trim()).then(() => setOutcome("")).catch(() => undefined); }}
              >
                {ACTION_LABELS.complete}
              </Button>
            </Flex>
          </Stack>
        </Card2>
      ) : null}
      {(can["record_sent"] && onRecordSent) || (can["record_return"] && onRecordReturn) || (can["withdraw"] && onWithdraw) ? (
        <Flex gap={10} wrap>
          {can["record_sent"] && onRecordSent ? (
            <Button tone="primary"  onClick={() => { setSentAt(businessLocalNow()); setDueAt(""); setDialog("sent"); }}>
              {ASSESSMENT_WORDS.record}
            </Button>
          ) : null}
          {can["record_return"] && onRecordReturn ? (
            <Button tone="primary"  onClick={() => { setReturnedAt(businessLocalNow()); setDialog("return"); }}>
              {ASSESSMENT_WORDS.record}
            </Button>
          ) : null}
          {can["withdraw"] && onWithdraw ? (
            <Button tone="ghost"  onClick={() => { setReason(""); setDialog("withdraw"); }}>
              {ASSESSMENT_STATUS.withdrawn}
            </Button>
          ) : null}
        </Flex>
      ) : null}
      {saved ? <Recorded live /> : null}

      <Dialog
        open={dialog === "sent"}
        title={ASSESSMENT_STATUS.sent}
        onClose={close}
        dismissLabel={ACTION_LABELS.cancel}
        refused={dialog === "sent" && refused ? { reason: refused } : undefined}
        primary={{
          label: ASSESSMENT_WORDS.record,
          busy,
          hs: "plan.assessment.chain.record-sent.confirm",
          blocked: sentAt ? undefined : { reason: COMMON_LABELS.required },
          onClick: () => { if (sentAt && onRecordSent) void onRecordSent(sentAt, dueAt).then(close).catch(() => undefined); },
        }}
      >
        <Stack gap={12}>
          <Field label={ASSESSMENT_STATUS.sent} required>
            <Input type="datetime-local" value={sentAt} max={businessLocalNow()}  onChange={(event) => setSentAt(event.target.value)} />
          </Field>
          <Field label={ASSESSMENT_WORDS.due} optional>
            <Input type="datetime-local" value={dueAt}  onChange={(event) => setDueAt(event.target.value)} />
          </Field>
        </Stack>
      </Dialog>

      <Dialog
        open={dialog === "return"}
        title={ASSESSMENT_STATUS.submitted}
        onClose={close}
        dismissLabel={ACTION_LABELS.cancel}
        refused={dialog === "return" && refused ? { reason: refused } : undefined}
        primary={{
          label: ASSESSMENT_WORDS.record,
          busy: busy || (upload.progress !== undefined && !fileReady && !upload.error),
          hs: "plan.assessment.chain.record-return.confirm",
          blocked: fileReady && returnedAt ? undefined : { reason: COMMON_LABELS.required },
          onClick: () => {
            if (fileReady && returnedAt && onRecordReturn) void onRecordReturn(returnedAt, [upload.fileId ?? ""]).then(close).catch(() => undefined);
          },
        }}
      >
        <Stack gap={12}>
          <DropZone
            label={ACTION_LABELS.upload}
            required
            fileName={upload.fileName}
            progress={fileReady ? undefined : upload.progress}
            error={upload.error}
            
            onFile={onFile}
          />
          <Field label={ASSESSMENT_STATUS.submitted} required>
            <Input type="datetime-local" value={returnedAt} max={businessLocalNow()}  onChange={(event) => setReturnedAt(event.target.value)} />
          </Field>
        </Stack>
      </Dialog>

      <Dialog
        open={dialog === "withdraw"}
        title={ASSESSMENT_STATUS.withdrawn}
        onClose={close}
        dismissLabel={ACTION_LABELS.cancel}
        refused={dialog === "withdraw" && refused ? { reason: refused } : undefined}
        primary={{
          label: ASSESSMENT_WORDS.record,
          busy,
          hs: "plan.assessment.chain.withdraw.confirm",
          blocked: reason.trim().length >= 3 ? undefined : { reason: COMMON_LABELS.required },
          onClick: () => { if (reason.trim().length >= 3 && onWithdraw) void onWithdraw(reason.trim()).then(close).catch(() => undefined); },
        }}
      >
        <Field label={G1_LABELS.withdrawalReason} required>
          <TextArea value={reason} maxLength={500} rows={3}  onChange={(event) => setReason(event.target.value)} />
        </Field>
      </Dialog>
    </Stack>,
    pill,
  );
}

/** The drop zone's cause for a refused upload (the scanner's three sentences). */
function dropError(error: unknown): DropZoneError {
  const code = error instanceof AppError ? `${error.code} ${error.message}`.toLowerCase() : "";
  if (code.includes("password")) return "passwordProtected";
  if (code.includes("unreadable") || code.includes("scan")) return "unreadable";
  return "invalid";
}

/**
 * The chain with its data path: `exercise.open` (absent for a seat it refuses: DC-28), the V1 record tools for Sent and
 * Submitted (the returned work uploaded first, as V1 did, `kind: exercise_return`), and S09's `exercise.review`,
 * `exercise.complete` and `exercise.withdraw`.
 */
export function AssessmentChain({ rid, aid }: { rid: string; aid: string }) {
  const q = useRead(["exercise", aid], () => exerciseOpen({ requisition_id: rid, application_id: aid }), { retry: false });
  const inval = [["exercise", aid], ["application", aid], ["today"]] as const;
  const sent = useWrite((input: { requisition_id: string; application_id: string; sent_at: string; due_at: string | null }, key: string) => exerciseRecordSent(input, key), inval);
  const back = useWrite((input: { requisition_id: string; application_id: string; returned_at: string; return_file_ids: string[] }, key: string) => exerciseRecordReturn(input, key), inval);
  const review = useWrite((input: ExerciseReviewInput, key: string) => exerciseReview(input, key), inval);
  const complete = useWrite((input: ExerciseCompleteInput, key: string) => exerciseComplete(input, key), inval);
  const withdraw = useWrite((input: ExerciseWithdrawInput, key: string) => exerciseWithdraw(input, key), inval);
  const [upload, setUpload] = useState<ReturnUpload>({});
  const writes = [sent, back, review, complete, withdraw];
  if (q.error && q.error.status === 404) return null;
  const error = writes.find((write) => write.error)?.error ?? null;
  const reset = () => { for (const write of writes) write.reset(); void q.refetch(); };
  const { refused, failure } = q.error ? failureOf(q.error, () => void q.refetch()) : failureOf(error, reset);
  const base = { requisition_id: rid, application_id: aid };
  const onFile = (file: File) => {
    setUpload({ fileName: file.name, progress: 10 });
    void uploadFile(file, { owner_type: "requisition", owner_id: rid, requisition_id: rid, kind: "exercise_return" }, {
      onPhase: (phase) => setUpload((old) => ({ ...old, progress: phase === "uploading" ? 40 : 80 })),
    }).then(
      (result) => setUpload({ fileName: file.name, fileId: result.file_id }),
      (cause: unknown) => setUpload({ fileName: file.name, error: dropError(cause) }),
    );
  };
  return (
    <McRoot>
      <AssessmentChainView
        data={q.data ? exerciseView(q.data) : null}
        loading={q.isLoading}
        busy={writes.some((write) => write.isPending)}
        refused={refused}
        failure={failure}
        saved={writes.some((write) => write.isSuccess)}
        upload={upload}
        onRecordSent={(sentAt, dueAt) => sent.mutateAsync({ ...base, sent_at: businessLocalToISO(sentAt), due_at: businessLocalToISO(dueAt) || null })}
        onFile={onFile}
        onRecordReturn={(returnedAt, fileIds) => back.mutateAsync({ ...base, returned_at: businessLocalToISO(returnedAt), return_file_ids: fileIds }).then((out) => { setUpload({}); return out; })}
        onReview={(draft) => review.mutateAsync({ ...base, ...draft } as ExerciseReviewInput)}
        onComplete={(text) => complete.mutateAsync({ ...base, completion_outcome: text } as ExerciseCompleteInput)}
        onWithdraw={(text) => withdraw.mutateAsync({ ...base, reason: text } as ExerciseWithdrawInput)}
      />
    </McRoot>
  );
}
